// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useState } from "react";
import { Box, Container, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { createStyleSheet } from "../utils/styles";
import TestFileUpload from "./TestFileUpload";
import { theme } from "../../theme";
import type { FieldEvaluationProps, ProjectModel } from "../types/projects";
import { useLazyGetProjectQuery } from "../features/data/projectApiSlice";
import { getStatusIcon } from "./StatusIcon";

const FieldEvaluation = (props: FieldEvaluationProps) => {
  const { selectedTest } = props;
  const { t } = useTranslation("FieldEvaluation");
  const [getProjectStatuses, { data }] = useLazyGetProjectQuery();
  const [isProgress, setIsProgress] = useState(false);

  const handleContinue = useCallback(() => {
    if (selectedTest?.projectUid) {
      getProjectStatuses({ projectUid: selectedTest.projectUid });
      setIsProgress(true);
    }
  }, [getProjectStatuses, setIsProgress, selectedTest]);

  const getStepsFromModel = (model: ProjectModel) => [
    {
      label: t("trainingYourModel"),
      status: model?.training?.status,
    },
    {
      label: t("validating"),
      status: model?.validation?.status,
    },
  ];

  return (
    <Container maxWidth="md" sx={styles.container}>
      {isProgress ? (
        <>
          <Typography variant="h4" fontWeight={500}>
            {t("inProgress")}
          </Typography>
          <Box sx={styles.evaluationBody} data-testid="evaluation-body">
            {data?.project.models?.map((model, index) => {
              const steps = getStepsFromModel(model);
              return (
                <Stack spacing={1.5} mt={2} key={index}>
                  {steps.map(step => (
                    <Box key={step.label} sx={styles.modelStepList}>
                      {getStatusIcon(step.status)}
                      <Typography ml={1}>{step.label}</Typography>
                    </Box>
                  ))}
                </Stack>
              );
            })}
          </Box>
        </>
      ) : (
        <Box>
          <Box sx={styles.headerContainer}>
            <Typography variant="h6" fontWeight={400}>
              {selectedTest?.name}
            </Typography>
            <Typography variant="h4" fontWeight={500}>
              {t("addFieldEvaluation")}
            </Typography>
          </Box>
          <Box sx={styles.fileUpload}>
            <Box>
              <Typography variant="body2">{selectedTest?.name}</Typography>
              <Typography variant="h6">{t("AddNewTest:testTitlePlaceholder")}</Typography>
            </Box>
            <TestFileUpload
              isFieldEvaluation={true}
              isThumbnailRequired={false}
              onContinue={handleContinue}
            />
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default FieldEvaluation;

const styles = createStyleSheet({
  container: {
    mt: 4,
  },
  headerContainer: {
    mb: 3,
  },
  fileUpload: {
    p: 3,
    border: `1px solid ${theme.palette.grey[300]}`,
    background: theme.palette.common.white,
    mb: 4,
  },
  evaluationBody: {
    background: "white",
    p: "20px",
  },
  modelStepList: {
    display: "flex",
    alignItems: "center",
  },
});
